import { computed, ref, watch } from 'vue';
import { SortItem } from '@/components/OverviewTable/types';
import { truthy } from './helper';
import { createEventHook } from '@vueuse/core';
import { OdataQuery } from './odata';
import { isEmpty, isEqual } from 'lodash';

export type ServerSidePaginationOptions<T extends string = string> = {
  page?: number;
  itemsPerPage?: number;
  sortBy?: SortItem<T>[];
};

export function useServerSideOdataPagination() {
  const page = ref<number | undefined>(undefined);
  const itemsPerPage = ref<number | undefined>(undefined);
  const sortBy = ref<SortItem[] | undefined>(undefined);
  const itemsLength = ref<number | undefined>(undefined);

  function reset() {
    updateQuery({
      page: undefined,
      itemsPerPage: undefined,
      sortBy: undefined,
    });
  }

  function setItemsLength(length: number | undefined) {
    itemsLength.value = length;
  }

  const onUpdateQuery = createEventHook<{
    query: OdataQuery<any> | undefined;
    setItemsLength: typeof setItemsLength;
  }>();

  function getOrderBy(sortOrder: SortItem[] | undefined) {
    return sortOrder?.map(({ key, order }: SortItem) =>
      [key.replace('.', '/'), order].filter(truthy).join(' '),
    );
  }

  function updateQuery(options: ServerSidePaginationOptions) {
    page.value = options.page;
    itemsPerPage.value = options.itemsPerPage;
    sortBy.value = options.sortBy;
  }

  const query = computed<OdataQuery<any> | undefined>(() => {
    return itemsPerPage.value && page.value
      ? {
          top: itemsPerPage.value,
          skip: (itemsPerPage.value ?? 0) * ((page.value ?? 1) - 1),
          orderBy: getOrderBy(sortBy.value),
        }
      : undefined;
  });

  watch(
    () => query.value,
    (newQuery, oldQuery) => {
      if (!isEmpty(newQuery) && !isEqual(newQuery, oldQuery)) {
        onUpdateQuery.trigger({ query: query.value, setItemsLength });
      }
    },
  );

  return {
    page,
    itemsPerPage,
    itemsLength,
    reset,
    updateQuery,
    onUpdateQuery: onUpdateQuery.on,
    sortBy,
    query,
  };
}
